import { useState } from 'react';
import { services } from '../data/content';
import { useEmail } from '../context/EmailContext';

export default function Services() {
  const [activeTab, setActiveTab] = useState(0);
  const currentService = services.content[activeTab];
  const { handleQuoteRequest, loadingId } = useEmail();

  return (
    <div className="bg-background min-h-screen">
      {/* Page Header */}
      <div className="bg-gradient-to-br from-primary-dark via-primary to-primary-600 text-white py-20">
        <div className="container-wide">
          <span className="inline-block text-sm font-semibold text-primary-200 uppercase tracking-wider mb-3">
            Our Expertise
          </span>
          <h1 className="text-4xl md:text-5xl font-bold">Services</h1>
          <p className="mt-4 text-primary-100 max-w-2xl text-lg">
            Comprehensive analytical testing and consulting services for the natural products industry.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container-wide py-16">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Tabs sidebar */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="card overflow-hidden sticky top-32 border border-gray-100">
              <div className="p-4 bg-gradient-subtle border-b border-gray-100">
                <span className="text-sm font-semibold text-text-light uppercase tracking-wider">
                  Select Service
                </span>
              </div>
              {services.tabs.map((tab, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTab(index)}
                  className={`w-full text-left px-5 py-4 border-b border-gray-50 last:border-b-0 transition-all duration-200 flex items-center gap-3 ${
                    activeTab === index
                      ? 'bg-primary text-white'
                      : 'bg-white text-text hover:bg-gray-50'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    activeTab === index ? 'bg-white' : 'bg-primary/30'
                  }`} />
                  <span className="font-medium text-sm">{tab}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content area */}
          <div className="flex-1">
            <div className="card p-8 md:p-10 border border-gray-100">
              <h2 className="text-2xl md:text-3xl font-bold text-text mb-2">
                {currentService.title}
              </h2>
              <div className="w-16 h-1 bg-gradient-to-r from-primary to-primary-400 rounded-full mb-8" />

              {currentService.image && (
                <div className="mb-8 rounded-2xl overflow-hidden shadow-soft">
                  <img
                    src={`/images/${currentService.image}`}
                    alt={currentService.title}
                    className="w-full h-auto object-cover"
                  />
                </div>
              )}

              <p className="text-text-light leading-relaxed mb-8 text-lg">
                {currentService.intro}
              </p>

              {/* Additional info for Training & Consulting */}
              {currentService.additionalInfo && (
                <div className="bg-primary-50 rounded-xl p-6 mb-8 border border-primary-100">
                  <p className="text-text-light leading-relaxed">
                    {currentService.additionalInfo}
                  </p>
                </div>
              )}

              {/* Sections for Analytical Testing */}
              {currentService.sections && (
                <div className="space-y-8 mb-8">
                  <h4 className="text-sm font-semibold text-primary uppercase tracking-wider">
                    We specialize in testing:
                  </h4>
                  <div className="grid gap-6">
                    {currentService.sections.map((section, index) => (
                      <div key={index} className="bg-gradient-subtle rounded-xl p-6 border border-gray-100">
                        <h3 className="text-lg font-semibold text-text mb-2">
                          {section.title}
                        </h3>
                        <p className="text-text-light leading-relaxed text-sm">
                          {section.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Supported services for Expert Testimony */}
              {currentService.supportedServices && (
                <div className="mb-8">
                  <h4 className="text-sm font-semibold text-primary uppercase tracking-wider mb-4">
                    Among our supported services are:
                  </h4>
                  <ul className="space-y-3">
                    {currentService.supportedServices.map((service, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3 text-text-light"
                      >
                        <svg
                          className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={() => handleQuoteRequest(currentService.title, 'services')}
                disabled={loadingId === 'services'}
                className="btn-accent relative"
              >
                <span className={`inline-flex items-center gap-2 ${loadingId === 'services' ? 'invisible' : ''}`}>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Request a Quote
                </span>
                {loadingId === 'services' && (
                  <svg className="w-5 h-5 animate-spin absolute inset-0 m-auto" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
